// Single source of truth for the per-instance dashboard's global time range.
// The page holds one InstanceRange (deep-linked via ?range=); every panel maps
// it to its endpoint's window via rangeToWindowHours. All three per-tenant
// series (health, AI spend, tool calls) take an `hours` window over the same
// allow-list {1, 4, 24, 168, 720}, so they always share one x-axis. 1h/4h give
// a fine-grained recent view (health is sample-dense there; the hourly tool-call
// source is coarser - see ToolCallTimelineService).

export type InstanceRange = "1h" | "4h" | "24h" | "7d" | "30d";

export type WindowHours = 1 | 4 | 24 | 168 | 720;

export const RANGE_OPTIONS: ReadonlyArray<{ value: InstanceRange; label: string }> = [
  { value: "1h", label: "1h" },
  { value: "4h", label: "4h" },
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
];

const VALID: ReadonlySet<string> = new Set(RANGE_OPTIONS.map((o) => o.value));

export const parseRange = (raw: string | null): InstanceRange =>
  raw !== null && VALID.has(raw) ? (raw as InstanceRange) : "24h";

export const rangeToWindowHours = (range: InstanceRange): WindowHours =>
  range === "1h" ? 1
    : range === "4h" ? 4
      : range === "24h" ? 24
        : range === "7d" ? 168
          : 720;

// "in the last 24 hours" style sublabel for KPI cards that aggregate over the
// selected window.
export const rangeWindowLabel = (range: InstanceRange): string =>
  range === "1h" ? "in 1h"
    : range === "4h" ? "in 4h"
      : range === "24h" ? "in 24h"
        : range === "7d" ? "in 7d"
          : "in 30d";

// The bare window, for prose that supplies its own preposition ("over the last
// 24h"). Read off RANGE_OPTIONS so the picker and the prose can never disagree.
export const rangeShortLabel = (range: InstanceRange): string =>
  RANGE_OPTIONS.find((option) => option.value === range)?.label ?? range;
